import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import { useAppSelector } from 'hooks/reduxHooks'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getIsLoyaltyAccountClosed from 'loyalty/selectors/getIsLoyaltyAccountClosed'
import { getBaseTierOfferWithJoiningFee, getLuxPlusSubscriptionRenewalDate } from 'selectors/luxPlusSelectors'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import LuxPlusManageAccountModalMembershipCancellation from './LuxPlusManageAccountModalMembershipCancellation'
import LuxPlusManageAccountModalOverview from './LuxPlusManageAccountModalOverview'
import LuxPlusManageAccountModalRenewalCancellation from './LuxPlusManageAccountModalRenewalCancellation'
import LuxPlusManageAccountModalRenewalCancellationSurvey from './LuxPlusManageAccountModalRenewalCancellationSurvey'
import LuxPlusManageAccountModalRenewalCancellationConfirmation from './LuxPlusManageAccountModalRenewalCancellationConfirmation'
import LuxPlusManageAccountModalKeepBenefits from './LuxPlusManageAccountModalKeepBenefits'
import LuxPlusManageAccountModalKeepPrice from './LuxPlusManageAccountModalKeepPrice'
import LuxPlusManageAccountModalRenewalBonusRetention from './LuxPlusManageAccountModalRenewalBonusRetention'
import LuxPlusManageAccountModalRenewalCancellationSuccess from './LuxPlusManageAccountModalRenewalCancellationSuccess'
import LuxPlusManageAccountModalRenewalCancellationSuccessTiered from './LuxPlusManageAccountModalRenewalCancellationSuccessTiered'
import LuxPlusManageAccountModalRenewalReactivation from './LuxPlusManageAccountModalRenewalReactivation'
import LuxPlusManageAccountModalRenewalReactivationSuccessTiered from './LuxPlusManageAccountModalRenewalReactivationSuccessTiered'
import LuxPlusManageAccountModalUpdatePaymentMethod from './LuxPlusManageAccountModalUpdatePaymentMethod'
import LuxPlusManageAccountModalUpdatePaymentMethodFailure from './LuxPlusManageAccountModalUpdatePaymentMethodFailure'
import LuxPlusManageAccountModalUpdatePaymentMethodSuccess from './LuxPlusManageAccountModalUpdatePaymentMethodSuccess'
import LuxPlusManageAccountModalAddBackupPaymentMethod from './LuxPlusManageAccountModalAddBackupPaymentMethod'

type ManageMode =
  | 'overview'
  | 'membership-cancellation'
  | 'renewal-cancellation'
  | 'renewal-cancellation-confirmation'
  | 'renewal-price-retention'
  | 'renewal-bonus-retention'
  | 'renewal-cancellation-success'
  | 'renewal-reactivation'
  | 'renewal-reactivation-success'
  | 'payment-method-update'
  | 'payment-method-update-success'
  | 'payment-method-update-failure'
  | 'backup-payment-method-add'

interface Props {
  initialMode?: ManageMode
  onBackupPaymentMethodUpdateSuccess?: (paymentMethodId?: string) => void
  onPaymentMethodUpdateSuccess?: () => void
  returnToOverviewOnActionComplete?: boolean
}

function LuxPlusManageAccountModal({
  initialMode = 'overview',
  onBackupPaymentMethodUpdateSuccess,
  onPaymentMethodUpdateSuccess,
  returnToOverviewOnActionComplete = true,
}: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const [mode, setMode] = useState<ManageMode>(initialMode)
  const [paymentFailureBackMode, setPaymentFailureBackMode] = useState<ManageMode>('payment-method-update')
  const [paymentMethodsRefreshKey, setPaymentMethodsRefreshKey] = useState(0)
  const [expectedBackupPaymentMethodId, setExpectedBackupPaymentMethodId] = useState<string>()

  const { item } = useAppSelector(state => state.auth.account.luxPlus.member.subscription)
  const renewalDate = useAppSelector(getLuxPlusSubscriptionRenewalDate)

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const isSocieteAccountClosed = useAppSelector(getIsLoyaltyAccountClosed)

  // Before the cancel-reason step, surface the price-retention modal when the member's renewal offer
  // is cheaper than the current new-member (active) offer — i.e. they'd lose a locked-in lower price.
  const activeOfferPrice = useAppSelector(getBaseTierOfferWithJoiningFee)?.price
  const shouldShowKeepPrice = isLoyaltyEnabled
    && !!item?.currentRenewalOffer
    && activeOfferPrice !== undefined
    && item.currentRenewalOffer.price < activeOfferPrice

  // After the price step, members see their renewal bonus before cancelling. The retention
  // screen invites non-Société members to join so they can receive it. The bonus is paid in Société
  // points, so a closed Société account (which can't receive it or join again) skips the bonus screens.
  const shouldShowRenewalBonus = isLoyaltyEnabled && !isSocieteAccountClosed

  // First retention step a "continue to cancel" from the confirmation lands on, skipping any
  // that don't apply, finally falling through to the cancel-reason form.
  const firstRetentionStep: ManageMode = shouldShowKeepPrice
    ? 'renewal-price-retention'
    : shouldShowRenewalBonus
      ? 'renewal-bonus-retention'
      : 'renewal-cancellation'

  const returnToOverviewOrDismiss = useCallback(() => {
    if (returnToOverviewOnActionComplete) {
      setMode('overview')
    } else {
      modalElement.resolve()
    }
  }, [modalElement, returnToOverviewOnActionComplete])

  return <ModalBase size="S" height="max" tabletHeight="auto">
    {!item && <ModalContent>
      <ModalBody>
        <LoadingIndicator inline>{intl.formatMessage({
          id: 'p8Cm7g3Ju1H67rSjBhKW',
          defaultMessage: 'Fetching account information',
          description: 'Loading indicator text while fetching account subscription data',
        })}
        </LoadingIndicator>
      </ModalBody>
    </ModalContent>}
    {!!item && <>
      {mode === 'overview' && <LuxPlusManageAccountModalOverview
        expectedBackupPaymentMethodId={expectedBackupPaymentMethodId}
        item={item}
        onCancelMembership={() => setMode('membership-cancellation')}
        onCancelRenew={() => setMode('renewal-cancellation-confirmation')}
        onExpectedBackupPaymentMethodFound={() => setExpectedBackupPaymentMethodId(undefined)}
        onReactivateRenew={() => setMode('renewal-reactivation')}
        onUpdatePaymentMethod={() => setMode('payment-method-update')}
        onAddBackupPaymentMethod={() => setMode('backup-payment-method-add')}
        paymentMethodsRefreshKey={paymentMethodsRefreshKey}
      />}
      {mode === 'membership-cancellation' && <LuxPlusManageAccountModalMembershipCancellation
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-cancellation-confirmation' && isLoyaltyEnabled && <LuxPlusManageAccountModalKeepBenefits
        item={item}
        onCancelRenewal={() => setMode(firstRetentionStep)}
      />}
      {mode === 'renewal-cancellation-confirmation' && !isLoyaltyEnabled && <LuxPlusManageAccountModalRenewalCancellationConfirmation
        item={item}
        onCancelRenewal={() => setMode(firstRetentionStep)}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-price-retention' && <LuxPlusManageAccountModalKeepPrice
        item={item}
        onContinueToCancel={() => setMode(shouldShowRenewalBonus ? 'renewal-bonus-retention' : 'renewal-cancellation')}
        onBack={() => setMode('renewal-cancellation-confirmation')}
      />}
      {mode === 'renewal-bonus-retention' && <LuxPlusManageAccountModalRenewalBonusRetention
        onContinueToCancel={() => setMode('renewal-cancellation')}
        onBack={() => setMode(shouldShowKeepPrice ? 'renewal-price-retention' : 'renewal-cancellation-confirmation')}
      />}
      {mode === 'renewal-cancellation' && isLoyaltyEnabled && <LuxPlusManageAccountModalRenewalCancellationSurvey
        item={item}
        onRenewCancelSuccess={() => setMode('renewal-cancellation-success')}
      />}
      {mode === 'renewal-cancellation' && !isLoyaltyEnabled && <LuxPlusManageAccountModalRenewalCancellation
        item={item}
        onRenewCancelSuccess={() => setMode('renewal-cancellation-success')}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-cancellation-success' && shouldShowRenewalBonus && <LuxPlusManageAccountModalRenewalCancellationSuccessTiered item={item} />}
      {mode === 'renewal-cancellation-success' && !shouldShowRenewalBonus && <LuxPlusManageAccountModalRenewalCancellationSuccess
        item={item}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-reactivation' && <LuxPlusManageAccountModalRenewalReactivation
        item={item}
        onReactivationSuccess={() => shouldShowRenewalBonus ? setMode('renewal-reactivation-success') : setMode('overview')}
        onBack={() => setMode('overview')}
      />}
      {/* Reactivation success only reached when the renewal bonus applies (see onReactivationSuccess above);
          non-loyalty brands and closed Société accounts return to overview, so there is no non-bonus success screen. */}
      {mode === 'renewal-reactivation-success' && <LuxPlusManageAccountModalRenewalReactivationSuccessTiered />}
      {mode === 'payment-method-update' && <LuxPlusManageAccountModalUpdatePaymentMethod
        item={item}
        renewalDate={renewalDate}
        onUpdatePaymentMethodSuccess={() => {
          setPaymentMethodsRefreshKey(refreshKey => refreshKey + 1)
          onPaymentMethodUpdateSuccess?.()
          setMode('payment-method-update-success')
        }}
        onUpdatePaymentMethodFailure={() => {
          setPaymentFailureBackMode('payment-method-update')
          setMode('payment-method-update-failure')
        }}
        onBack={returnToOverviewOrDismiss}
      />}
      {mode === 'payment-method-update-success' && <LuxPlusManageAccountModalUpdatePaymentMethodSuccess
        onBack={returnToOverviewOrDismiss}
      />}
      {mode === 'payment-method-update-failure' && <LuxPlusManageAccountModalUpdatePaymentMethodFailure
        onBack={() => setMode(paymentFailureBackMode)}
      />}
      {mode === 'backup-payment-method-add' && <LuxPlusManageAccountModalAddBackupPaymentMethod
        item={item}
        renewalDate={renewalDate}
        onSuccess={(paymentMethodId) => {
          setExpectedBackupPaymentMethodId(paymentMethodId)
          setPaymentMethodsRefreshKey(refreshKey => refreshKey + 1)
          onBackupPaymentMethodUpdateSuccess?.(paymentMethodId)
          returnToOverviewOrDismiss()
        }}
        onFailure={() => {
          setPaymentFailureBackMode('backup-payment-method-add')
          setMode('payment-method-update-failure')
        }}
        onBack={returnToOverviewOrDismiss}
      />}
    </>}
  </ModalBase>
}

export default LuxPlusManageAccountModal
